import { size } from '@/public/template/common/css/setting/variable';
import ThumbnailHelper, {
  DEFAULT_IMAGE_19x10,
  DEFAULT_IMAGE_1x1,
  DEFAULT_IMAGE_4x3
} from '@/public/template/common/thumbnail/ThumbnailHelper';

export interface ImageSize {
  readonly height?: string;
  readonly width?: string;
}

/**
 * 定義済みの画像サイズ定数。
 */
export const ImageSizeConstants = {
  /** オーバーレイリンク内で使われるサイズ */
  SP_XSMALL: 'sp_xsmall',
  /** トピックスなどで使われるサイズ */
  SP_SMALL: 'sp_small',
  /** 右カラム、トピ一覧などで使われるサイズ */
  PC_SMALL: 'pc_small',
  /** ニュースフィードなどで使われるサイズ（SP） */
  SP_MIDDLE: 'sp_middle',
  /** ニュースフィードなどで使われるサイズ（PC） */
  PC_MIDDLE: 'pc_middle',
  /** 速報などで使われるサイズ */
  PC_LARGE: 'pc_large',
  /** ニュースフィードの大サイズなどで使われるサイズ（SP） */
  SP_WIDE: 'sp_wide',
  /** ニュースフィードの横長版などで使われる横幅いっぱいサイズ（PC） */
  PC_WIDE: 'pc_wide',
  /** ヤフコメタイムラインなどで使われるサイズ（SP） */
  SP_WIDE_19X10: 'sp_wide_19x10',
  /** ヤフコメタイムラインなどで使われるサイズ（PC） */
  PC_WIDE_19X10: 'pc_wide_19x10',
  /** トピックスのワイド画像比率（1:2.414）で左右の余白は速報版 */
  SP_TOPICSRATIO_FLASH: 'sp_topicsRatio_flash',
  /** トピックスのワイド画像比率（1:2.414）で左右の余白はトップ版 */
  SP_TOPICSRATIO_TOP: 'sp_topicsRatio_top',
  /** 横幅100%で高さはautoなサイズ（最大幅あり。CSSで上書きしている） */
  SP_PICKUP: 'sp_pickup'
} as const;

export type ImageSizeConstants = (typeof ImageSizeConstants)[keyof typeof ImageSizeConstants];

export interface MultiSrc {
  readonly jpeg: string;
  readonly webp: string;
}

export const isMultiSrc = (src: string | MultiSrc | undefined): src is MultiSrc => {
  return src !== undefined && typeof src !== 'string' && 'jpeg' in src && 'webp' in src;
};

/**
 * 画像の幅と高さを取得する
 *
 * @param imageSize 画像のサイズを指定する
 */
export const getImageWidthAndHeight = (imageSize: ImageSize | ImageSizeConstants | undefined) => {
  if (imageSize === undefined) {
    return undefined;
  }

  // 画像サイズが数値で指定されている場合はそのまま返す
  if (typeof imageSize === 'object' && imageSize.width && imageSize.height) {
    return imageSize;
  }

  if (typeof imageSize === 'string') {
    switch (imageSize) {
      case ImageSizeConstants.PC_SMALL:
        return { width: size.thumbnail_small_pc, height: size.thumbnail_small_pc };
      case ImageSizeConstants.PC_MIDDLE:
        return { width: size.thumbnail_middle_pc, height: size.thumbnail_middle_pc };
      case ImageSizeConstants.PC_LARGE:
        return { width: size.thumbnail_large_pc, height: size.thumbnail_large_pc };
      case ImageSizeConstants.PC_WIDE:
        return { width: size.thumbnail_wide_width_pc, height: size.thumbnail_wide_height_pc };
      case ImageSizeConstants.PC_WIDE_19X10:
        return { width: size.thumbnail_wide_19X10_width_pc, height: size.thumbnail_wide_19X10_height_pc };
      case ImageSizeConstants.SP_XSMALL:
        return { width: size.thumbnail_xsmall_sp, height: size.thumbnail_xsmall_sp };
      case ImageSizeConstants.SP_SMALL:
        return { width: size.thumbnail_small_sp, height: size.thumbnail_small_sp };
      case ImageSizeConstants.SP_MIDDLE:
        return { width: size.thumbnail_middle_sp, height: size.thumbnail_middle_sp };
      case ImageSizeConstants.SP_WIDE:
      case ImageSizeConstants.SP_WIDE_19X10:
        return { width: size.thumbnail_wide_width_sp, height: size.thumbnail_wide_height_sp };
      case ImageSizeConstants.SP_TOPICSRATIO_FLASH:
        return { width: size.thumbnail_topicsRatio_flash_width_sp, height: size.thumbnail_topicsRatio_flash_height_sp };
      case ImageSizeConstants.SP_TOPICSRATIO_TOP:
        return { width: size.thumbnail_topicsRatio_top_width_sp, height: size.thumbnail_topicsRatio_top_height_sp };
      case ImageSizeConstants.SP_PICKUP:
        return { width: size.thumbnail_pickup_width_sp, height: size.thumbnail_pickup_height_sp };
      default:
        return undefined;
    }
  }

  return undefined;
};

/**
 * 画像が読み込めない時に表示するデフォルト画像のURLを取得する
 *
 * @param imageSize 画像のサイズを指定する
 */
export const getFallbackImageSrc = (imageSize: ImageSize | ImageSizeConstants | undefined): string => {
  if (imageSize === undefined) {
    return DEFAULT_IMAGE_1x1;
  }

  // 画像サイズが数値で指定されている場合はThumbnailHelperからデフォルト画像を取得
  if (typeof imageSize === 'object' && imageSize.width && imageSize.height) {
    return ThumbnailHelper.getDefaultImage(imageSize.width.toString(), imageSize.height.toString());
  }

  if (typeof imageSize === 'string') {
    switch (imageSize) {
      case ImageSizeConstants.SP_WIDE_19X10:
      case ImageSizeConstants.PC_WIDE_19X10:
        return DEFAULT_IMAGE_19x10;
      case ImageSizeConstants.SP_WIDE:
      case ImageSizeConstants.PC_WIDE:
      case ImageSizeConstants.SP_TOPICSRATIO_FLASH:
      case ImageSizeConstants.SP_TOPICSRATIO_TOP:
      case ImageSizeConstants.SP_PICKUP:
        return DEFAULT_IMAGE_4x3;
      case ImageSizeConstants.SP_SMALL:
      case ImageSizeConstants.PC_SMALL:
      case ImageSizeConstants.SP_MIDDLE:
      case ImageSizeConstants.PC_MIDDLE:
      case ImageSizeConstants.PC_LARGE:
      default:
        return DEFAULT_IMAGE_1x1;
    }
  }

  return DEFAULT_IMAGE_1x1;
};
